import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { C, FONT, SectionHeader, MarkerChip, AddMarkerControl, DEFAULT_MARKER_NAME, findMarkerByName } from './adminShared';
import { useAuth } from '../../hooks/useAuth';
import { useMarkers, deleteMarker, markerRangeId } from './timeframeMarkers';
import { useSalesVideoWindows, salesWindow, salesExtra } from './salesVideoPeriods';
import {
  getAdminLeadsStats, getAdminFunnelPageStats,
  getAdminRySalesStats, getAdminWistiaStats,
} from '../../utils/api';

// ─── COMPARE ─────────────────────────────────────────────────────────────────
// The Ad Metrics figures, split into equal blocks side by side, so Ben can see
// whether the funnel is moving. PAID TRAFFIC ONLY, always — there is no
// all-traffic toggle here.
//
// ⚠ NO NEW COUNTING LOGIC. Every figure comes from the same four endpoints the
// Ad Metrics tab calls, with a different from/to. That is the entire point: a
// column can never disagree with Ad Metrics, because nothing here counts
// anything. The only arithmetic done in this file is the same browser-side
// rates AdminPages.jsx already does, copied formula-for-formula (see RATE
// FORMULAS below).
//
// ⚠ BLOCKS ARE ROLLING AND ANCHORED AT ONE INSTANT. They step backwards in
// exact equal durations from a single `anchor` captured when the period is
// chosen. No calendar snapping — not to midnight, not to Mondays, not to the
// 1st. Every block is exactly as long as every other, so no column is a partial
// block that reads as a false drop. This is why the requests send FULL ISO
// TIMESTAMPS: parseRangeParam (backend/src/config/launch.js:68-75) day-snaps a
// bare YYYY-MM-DD and uses a full timestamp as given. Sending bare dates would
// silently destroy the premise of this tab.
//
// ⚠ LATENT TRAP, DELIBERATELY LEFT ALONE. OPTIN_PRETRACKING_BASELINE is added
// to optin.pageLoads and optin.uniqueVisitors at backend/src/routes/admin.js:
// 1842-1843, independently of the window. It is 0 today (launch.js:102-105) so
// nothing here is wrong. But it is env-overridable, and if it were ever set it
// would add the SAME constant to EVERY column and quietly corrupt every
// comparison on this tab. If these two rows ever look uniformly inflated, check
// that env var first.

// ── THE LAUNCH FLOOR, MIRRORED ───────────────────────────────────────────────
// ⚠ THIS IS A COPY of LAUNCH_DATE in backend/src/config/launch.js:32. If that
// value ever changes, THIS MUST CHANGE WITH IT or this tab will offer periods
// the backend cannot answer.
//
// It matters because floorFrom (launch.js:42-45) SILENTLY RAISES any earlier
// ?from up to the floor — it does not reject and it does not warn. A block
// starting before the floor would return a shorter window than its label
// claims, look like a real column, and be wrong.
const LAUNCH_FLOOR_ISO = '2026-08-14T00:00:00.000Z';
const LAUNCH_FLOOR_MS  = Date.parse(LAUNCH_FLOOR_ISO);

// ── WHEN SALES ATTRIBUTION BEGAN ─────────────────────────────────────────────
// Two days after the floor. Its twin is on screen at
// frontend/src/pages/admin/AdminPages.jsx:678 ("Sales attribution runs from
// 16 Aug 2026 forward — earlier buyers carry no visitor id"). Keep the two in
// step.
//
// A column ending before this instant must show a DASH on the sales rows, not
// a zero: a zero with a down arrow beside it reads as "sales collapsed" when
// the truth is "we were not measuring yet".
const SALES_FROM_ISO = '2026-08-16T00:00:00.000Z';
const SALES_FROM_MS  = Date.parse(SALES_FROM_ISO);

const DAY = 24 * 60 * 60 * 1000;

// blocks x blockMs. "Months" are 30-day blocks, not calendar months — equal
// length is what makes the columns comparable.
const PERIODS = [
  { id: '7d',  label: 'Last 7 days',    blocks: 7,  blockMs: DAY,      unit: 'day'   },
  { id: '2w',  label: 'Last 2 weeks',   blocks: 2,  blockMs: 7 * DAY,  unit: 'week'  },
  { id: '3w',  label: 'Last 3 weeks',   blocks: 3,  blockMs: 7 * DAY,  unit: 'week'  },
  { id: '4w',  label: 'Last 4 weeks',   blocks: 4,  blockMs: 7 * DAY,  unit: 'week'  },
  { id: '3m',  label: 'Last 3 months',  blocks: 3,  blockMs: 30 * DAY, unit: 'month' },
  { id: '4m',  label: 'Last 4 months',  blocks: 4,  blockMs: 30 * DAY, unit: 'month' },
  { id: '6m',  label: 'Last 6 months',  blocks: 6,  blockMs: 30 * DAY, unit: 'month' },
  { id: '12m', label: 'Last 12 months', blocks: 12, blockMs: 30 * DAY, unit: 'month' },
];

const totalMs = (p) => p.blocks * p.blockMs;
/** A period is offered only once its OLDEST block starts on or after the floor. */
const availableFromMs = (p) => LAUNCH_FLOOR_MS + totalMs(p);
const isAvailable = (p, anchorMs) =>
  p.marker ? isMarkerAvailable(p, anchorMs) : anchorMs - totalMs(p) >= LAUNCH_FLOOR_MS;

// ── A NAMED MARKER AS A PERIOD ──────────────────────────────────────────────
// Ben's decision: on Compare a marker sets the START, and the span from that
// date to now is split into columns. The column length is chosen from the span
// so the grid stays readable — between roughly 3 and 12 columns:
//   span ≤ 14 days            → daily
//   14 days < span ≤ 84 days  → weekly   (12 weeks; the "~4 months" in the brief
//                                         would be 17 columns, so it is 3 months)
//   span > 84 days            → monthly (30-day, like the built-in months)
// Daily can reach 14 columns at the top of its band; every other band stays
// within 3–12.
const markerStartMs = (m) => Date.parse(`${m.date}T00:00:00.000Z`);
// A marker cannot be created before the floor (the picker forbids it), but a
// row edited by hand could be — then the period is greyed, never rendered wrong.
const isMarkerAvailable = (p, anchorMs) => {
  const start = markerStartMs(p.marker);
  return Number.isFinite(start) && start >= LAUNCH_FLOOR_MS && start < anchorMs;
};
const markerPeriod = (m) => ({ id: markerRangeId(m), label: m.name, marker: m });

function chunkFor(spanMs) {
  if (spanMs <= 14 * DAY) return { ms: DAY,      unit: 'day',   word: 'daily' };
  if (spanMs <= 84 * DAY) return { ms: 7 * DAY,  unit: 'week',  word: 'weekly' };
  return                         { ms: 30 * DAY, unit: 'month', word: 'monthly (30-day)' };
}

/**
 * Blocks from the marker date forward to now. Full chunks are anchored at NOW
 * and step back, exactly like buildBlocks, so the most recent column is always
 * a full chunk — a short final column would read as a false drop.
 *
 * THE REMAINDER GOES AT THE START. If it is at least half a chunk it becomes
 * its own short first column, tagged "partial" with its real length; if it is
 * less than half a chunk it is folded into the first full column, which is
 * then tagged with its real length too. Either way the marker date is the
 * left edge and no column is silently the wrong size.
 */
function buildMarkerBlocks(m, anchorMs) {
  const startMs = markerStartMs(m);
  const spanMs  = anchorMs - startMs;
  const chunk   = chunkFor(spanMs);
  const full    = Math.floor(spanMs / chunk.ms);
  const remMs   = spanMs - full * chunk.ms;
  const mk = (fromMs, toMs, back, extra = {}) => ({
    back, fromMs, toMs, isLatest: back === 0,
    fromISO: new Date(fromMs).toISOString(), toISO: new Date(toMs).toISOString(),
    lengthMs: toMs - fromMs, ...extra,
  });
  const out = [];
  if (full === 0) {
    // Marker set today: the whole span is under one chunk.
    out.push(mk(startMs, anchorMs, 0, { partial: true }));
    return { blocks: out, chunk };
  }
  const foldRemainder = remMs > 0 && remMs < chunk.ms / 2;
  if (remMs > 0 && !foldRemainder) out.push(mk(startMs, startMs + remMs, full, { partial: true }));
  for (let back = full - 1; back >= 0; back--) {
    const toMs = anchorMs - back * chunk.ms;
    let fromMs = toMs - chunk.ms;
    const extra = {};
    if (back === full - 1 && foldRemainder) { fromMs = startMs; extra.extended = true; }
    out.push(mk(fromMs, toMs, back, extra));
  }
  return { blocks: out, chunk };
}
const fmtLen = (ms) => { const d = ms / DAY; return `${d < 10 ? Math.round(d * 10) / 10 : Math.round(d)}d`; };

const fmtShort = (ms) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const fmtFull  = (ms) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * OLDEST FIRST, so the grid reads as a timeline: left to right is forwards in
 * time and the most recent block is on the right.
 *
 * `back` is how many blocks back from now this one is — 0 is the current block.
 * The ARRAY position is what the screen shows; `back` is what the block means.
 * Keeping both means the fetch order can start with the newest column (the one
 * Ben looks at first) while the render order stays chronological.
 */
function buildBlocks(period, anchorMs) {
  const out = [];
  for (let back = period.blocks - 1; back >= 0; back--) {
    const toMs   = anchorMs - back * period.blockMs;
    const fromMs = toMs - period.blockMs;
    out.push({
      back, fromMs, toMs,
      isLatest: back === 0,
      fromISO: new Date(fromMs).toISOString(),
      toISO:   new Date(toMs).toISOString(),
    });
  }
  return out;
}

// ── RATE FORMULAS — COPIED, NOT REINVENTED ───────────────────────────────────
// Twins of AdminPages.jsx:52 (pctOf) and :31-35 (optinRate). They are duplicated
// rather than imported because AdminPages does not export them and this build
// is not permitted to modify that file beyond a label fix. If either formula
// changes there, it MUST change here — two places computing the same rate
// differently is exactly how a comparison tab becomes untrustworthy.
const pctOf = (count, denom) => (denom > 0 ? Math.round((count / denom) * 100) : 0);
const optinRateValue = (numerator, denominator) => {
  const n = Number(numerator), d = Number(denominator);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) return null;
  return (n / d) * 100;
};
// AdminPages.jsx:35 — one decimal below 10%, whole numbers above.
const optinRateText = (v) => (v == null ? '—' : `${v < 10 ? v.toFixed(1) : Math.round(v)}%`);
// Twin of AdminPages' shareOfOptins, minus its " of opt-ins" suffix: a dash at
// zero opt-ins (the value is already null by then), '0%' for exactly zero —
// not '0.0%', there is nothing to separate at zero — one decimal below 10%,
// and '>100%' above 100, capping the label and never the value.
const shareOfOptinsText = (v) => {
  if (v == null) return '—';
  if (v > 100) return '>100%';
  if (v === 0) return '0%';
  return `${v < 10 ? v.toFixed(1) : Math.round(v)}%`;
};

const num = (v) => (v == null ? null : Number(v));

// ── THE ROWS ─────────────────────────────────────────────────────────────────
// Taken from what AdminPages.jsx actually renders on the Ad Metrics tab, in its
// order and with its labels. `kind` decides how the change is expressed:
//   count → percentage change
//   rate  → percentage POINTS (14% -> 17% is "+3 pts", never "+21%")
// `sales: true` marks a row sourced from /ry-sales-stats, which cannot report
// anything before SALES_FROM_ISO.
// `count` (optional) is a second getter for a rate row: the raw numerator,
// rendered as the primary figure with the rate beside it. The row's value —
// what the change arrow measures — is still `get`.
const SECTIONS = [
  {
    id: 'optin',
    title: 'Opt-in page',
    // The Starting 5 asymmetry, in one line rather than the full paragraph at
    // AdminPages.jsx:756-760, which will not fit a dense grid.
    footnote: 'Landings count the Starting 5 campaign only; opt-ins count every campaign, so these rates read slightly high.',
    rows: [
      { label: 'Page loads',               kind: 'count', get: d => num(d.funnel?.optin?.pageLoads) },
      { label: 'Unique visitors',          kind: 'count', get: d => num(d.funnel?.optin?.uniqueVisitors) },
      { label: 'Opt-ins',                  kind: 'count', get: d => num(d.leads?.totalLeads) },
      { label: 'Page loads → opt-ins',     kind: 'rate', decimalsUnder10: true,
        get: d => optinRateValue(d.leads?.totalLeads, d.funnel?.optin?.pageLoads) },
      { label: 'Unique visitors → opt-ins', kind: 'rate', decimalsUnder10: true,
        get: d => optinRateValue(d.leads?.totalLeads, d.funnel?.optin?.uniqueVisitors) },
    ],
  },
  {
    id: 'masterclass',
    title: 'Masterclass',
    rows: [
      { label: 'Page loads',       kind: 'count', get: d => num(d.wistia?.masterclass?.pageLoads) },
      { label: 'Unique visitors',  kind: 'count', get: d => num(d.wistia?.masterclass?.uniqueVisitorsAll) },
      { label: 'Engaged visitors', kind: 'count', get: d => num(d.wistia?.masterclass?.totalSessions) },
      { label: 'Started watching', kind: 'count', get: d => num(d.wistia?.masterclass?.totalPlays) },
      // AdminPages.jsx:819-820 — denominator is uniqueVisitorsAll, named once.
      { label: 'Watch rate',  kind: 'rate', get: d => rateOr(d.wistia?.masterclass, m => pctOf(m.totalPlays, m.uniqueVisitorsAll ?? 0)) },
      // ── THE CTA ROWS CARRY THEIR RAW COUNT AS WELL ──────────────────────
      // `count` is the numerator of the percentage, shown as the primary figure
      // with the rate beside it — the Ad Metrics tiles read "43 of 94 unique
      // visitors" and this is the same pair. The change arrow stays on the
      // PERCENTAGE (kind: 'rate' → points), never on the count.
      { label: 'Reached CTA', kind: 'rate',
        count: d => num(d.wistia?.masterclass?.cta?.revealed?.count),
        get:   d => rateOr(d.wistia?.masterclass, m => (m.cta ? pctOf(m.cta.revealed.count, m.uniqueVisitorsAll ?? 0) : null)) },
      // Clicked CTA is TWO rows with the SAME numerator. The count is therefore
      // the same number in both — that is correct: only the denominator differs.
      { label: 'Clicked CTA (of visitors)', kind: 'rate',
        count: d => num(d.wistia?.masterclass?.cta?.clicked?.count),
        get:   d => rateOr(d.wistia?.masterclass, m => (m.cta ? pctOf(m.cta.clicked.count,  m.uniqueVisitorsAll ?? 0) : null)) },
      // Against OPT-INS for the same column — d.leads.totalLeads, the figure the
      // Opt-ins row above already reads. Rendered by shareOfOptinsText, a twin
      // of AdminPages' shareOfOptins: a dash when opt-ins are zero
      // (optinRateValue returns null, never 0% or NaN), '0%' at exactly zero,
      // one decimal below 10%, and a '>100%' cap on the LABEL only — the
      // masterclass link works without an opt-in, so clickers can legitimately
      // outnumber opt-ins and the underlying value stays uncapped.
      { label: 'Clicked CTA (of opt-ins)', kind: 'rate', shareOfOptins: true,
        count: d => num(d.wistia?.masterclass?.cta?.clicked?.count),
        get:   d => rateOr(d.wistia?.masterclass, m => (m.cta ? optinRateValue(m.cta.clicked.count, d.leads?.totalLeads) : null)) },
      // A BAND, not "reached at least": started watching but under 25% seen.
      // Every milestone count comes from the started set, so this + 25% seen =
      // Started watching exactly (AdminPages under25Of). Same denominator below.
      { label: 'Under 25% seen', kind: 'rate', get: d => rateOr(d.wistia?.masterclass, m => pctOf(m.totalPlays - m.milestones.p25.count, m.uniqueVisitorsAll ?? 0)) },
      { label: '25% seen', kind: 'rate', get: d => rateOr(d.wistia?.masterclass, m => pctOf(m.milestones.p25.count, m.uniqueVisitorsAll ?? 0)) },
      { label: '50% seen', kind: 'rate', get: d => rateOr(d.wistia?.masterclass, m => pctOf(m.milestones.p50.count, m.uniqueVisitorsAll ?? 0)) },
      { label: '75% seen', kind: 'rate', get: d => rateOr(d.wistia?.masterclass, m => pctOf(m.milestones.p75.count, m.uniqueVisitorsAll ?? 0)) },
      { label: '90% seen', kind: 'rate', get: d => rateOr(d.wistia?.masterclass, m => pctOf(m.milestones.p90.count, m.uniqueVisitorsAll ?? 0)) },
      { label: '95%+ seen', kind: 'rate', get: d => rateOr(d.wistia?.masterclass, m => pctOf(m.milestones.end.count, m.uniqueVisitorsAll ?? 0)) },
    ],
  },
  {
    id: 'sales',
    title: 'Sales page',
    rows: [
      { label: 'Page loads',       kind: 'count', get: d => num(d.funnel?.pageViews) },
      { label: 'Unique visitors',  kind: 'count', get: d => num(d.funnel?.uniqueViewers) },
      { label: 'Engaged visitors', kind: 'count', get: d => num(d.funnel?.uniqueVisitors) },
      // AdminPages.jsx:974-978 — clickers over uniqueViewers, never clicks.
      { label: 'Clicked a CTA', kind: 'rate',
        get: d => (d.funnel ? pctOf(d.funnel.checkout?.ctaClickSessions ?? 0, d.funnel.uniqueViewers ?? 0) : null) },
      { label: 'CTA clicks (raw)', kind: 'count',
        get: d => (d.funnel?.ctaByText ? d.funnel.ctaByText.reduce((s, r) => s + r.clicks, 0) : null) },
      { label: 'Reached at least 25%', kind: 'rate', get: d => rateOr(d.funnel, f => pctOf(f.scroll?.p25?.count || 0, f.uniqueViewers ?? 0)) },
      { label: 'Reached at least 50%', kind: 'rate', get: d => rateOr(d.funnel, f => pctOf(f.scroll?.p50?.count || 0, f.uniqueViewers ?? 0)) },
      { label: 'Reached at least 75%', kind: 'rate', get: d => rateOr(d.funnel, f => pctOf(f.scroll?.p75?.count || 0, f.uniqueViewers ?? 0)) },
      { label: 'Reached at least 90%', kind: 'rate', get: d => rateOr(d.funnel, f => pctOf(f.scroll?.p90?.count || 0, f.uniqueViewers ?? 0)) },
      // Sales-page VIDEO milestones — ONE ROW PER VIDEO (v1 and v2 are different
      // lengths and are never blended). Each column counts a video only over
      // its own live part of that column, from its own records, divided by the
      // people who STARTED watching it there (ry.totalPlays) — the sales card's
      // bars since 2026-10-21 (AdminPages SalesVideoBlock). A dash = that video
      // was not on the page in that column, or nobody started it.
      // A band: pressed play but under 25% seen (videoRate 'under25').
      { label: 'Video under 25% watched (of starters)', kind: 'rate', perVideo: 'under25' },
      { label: 'Video 25% watched (of starters)', kind: 'rate', perVideo: 'p25' },
      { label: 'Video 50% watched (of starters)', kind: 'rate', perVideo: 'p50' },
      { label: 'Video 75% watched (of starters)', kind: 'rate', perVideo: 'p75' },
      { label: 'Video 90% watched (of starters)', kind: 'rate', perVideo: 'p90' },
      { label: 'Video reached end (of starters)', kind: 'rate', perVideo: 'end' },
      // Checkout funnel — AdminPages.jsx:581-593. People, not actions.
      { label: 'Checkout opened',       kind: 'count', get: d => num(d.funnel?.checkout?.openedSessions) },
      { label: "Couldn't open checkout", kind: 'count', hideIfAllZero: true, get: d => num(d.funnel?.checkout?.openFailedSessions) },
      { label: 'Details submitted',     kind: 'count', get: d => num(d.funnel?.checkout?.submittedSessions) },
      { label: 'Payment failed',        kind: 'count', get: d => num(d.funnel?.checkout?.failedSessions) },
      { label: 'Already enrolled',      kind: 'count', hideIfAllZero: true, get: d => num(d.funnel?.checkout?.alreadyEnrolledSessions) },
      { label: 'Purchased',             kind: 'count', get: d => num(d.funnel?.checkout?.purchasedSessions) },
      // /ry-sales-stats — cannot report before 16 Aug 2026.
      { label: 'Sales from ads', kind: 'count', sales: true, get: d => num(d.sales?.paidPurchasers) },
      { label: 'Enrolled',       kind: 'count', sales: true, get: d => num(d.sales?.totalPurchasers) },
      { label: 'Gift',           kind: 'count', sales: true, get: d => num(d.sales?.giftPurchasers) },
      { label: 'Comp',           kind: 'count', sales: true, get: d => num(d.sales?.compPurchasers) },
    ],
  },
];


function rateOr(obj, fn) { try { return obj ? fn(obj) : null; } catch { return null; } }
// COPY of AdminPages SalesVideoBlock's rule: a share of those who started
// watching (ry.totalPlays), null (a dash) when nobody did. Change both together.
function videoRate(d, key) {
  const ry = d.wistia?.ryLanding;
  const started = ry?.totalPlays ?? 0;
  if (!ry?.milestones?.p25 || !(started > 0)) return null;
  if (key === 'under25') return pctOf(started - ry.milestones.p25.count, started);
  const m = ry.milestones[key];
  if (!m) return null;
  return pctOf(m.count, started);
}

// ── THE CHANGE INDICATOR ─────────────────────────────────────────────────────
// ⚠ THE OLDER BLOCK IS THE COLUMN TO THE LEFT. Columns run oldest-to-newest, so
// each one is measured against its LEFT neighbour. The comparison itself has not
// changed — every block is still measured against the block immediately older
// than it — only which side of the screen that block sits on. Get this backwards
// and every arrow on the tab points the wrong way while looking entirely normal.
//
// Counts move in percent; rates move in POINTS. 14% -> 17% is "+3 pts" — never
// "+21%", which is a percentage of a percentage and means nothing here.
//
// SUPPRESSION: below 5 in the OLDER column there is no honest change to report.
// 1 -> 2 rendering as "+100%" would be the loudest and least useful thing on
// the page, so it shows a dash instead.
const MIN_OLD_FOR_CHANGE = 5;

function changeFor(row, newVal, oldVal) {
  if (newVal == null || oldVal == null) return null;
  if (row.kind === 'rate') {
    // Points are already on a 0-100 scale, so the suppression floor applies to
    // the rate itself: under 5% either side is noise.
    if (oldVal < MIN_OLD_FOR_CHANGE) return null;
    const diff = newVal - oldVal;
    const dp = row.decimalsUnder10 && newVal < 10 && oldVal < 10 ? 1 : 0;
    if (Math.abs(diff) < (dp ? 0.05 : 0.5)) return { dir: 0, text: 'no change' };
    return { dir: Math.sign(diff), text: `${diff > 0 ? '+' : '−'}${Math.abs(diff).toFixed(dp)} pts` };
  }
  if (oldVal < MIN_OLD_FOR_CHANGE) return null;   // includes 0 — undefined change
  const pct = Math.round(((newVal - oldVal) / oldVal) * 100);
  if (pct === 0) return { dir: 0, text: 'no change' };
  return { dir: Math.sign(pct), text: `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%` };
}

const Change = ({ change }) => {
  if (!change) return <span style={{ fontSize: 9.5, color: C.border }}>—</span>;
  if (change.dir === 0) return <span style={{ fontSize: 9.5, color: C.accent, opacity: 0.7 }}>no change</span>;
  // Muted on purpose. This is a scanning tool, not an alarm — no bright blocks.
  const up = change.dir > 0;
  return (
    <span style={{ fontSize: 9.5, color: up ? '#4F7A52' : '#A8674F', opacity: 0.9, whiteSpace: 'nowrap' }}>
      {up ? '▲' : '▼'} {change.text}
    </span>
  );
};

// ── LAYOUT ───────────────────────────────────────────────────────────────────
// One horizontally scrolling surface. The label cell in every row is
// position:sticky left:0, so the metric names stay pinned while the columns
// scroll under them. Text stays at normal size — shrinking it to fit a 375px
// phone would make the whole tab unreadable, which defeats the point.
const LABEL_W = 132;
const COL_W   = 96;

const labelCell = {
  flex: `0 0 ${LABEL_W}px`, width: LABEL_W, position: 'sticky', left: 0, zIndex: 2,
  background: C.card, borderRight: `1px solid ${C.border}`,
  padding: '7px 8px', boxSizing: 'border-box',
};
const dataCell = {
  flex: `0 0 ${COL_W}px`, width: COL_W, padding: '7px 8px',
  boxSizing: 'border-box', textAlign: 'right',
};

export const AdminCompare = () => {
  const vw = useSalesVideoWindows(undefined, undefined);   // every sales video, for the per-video rows
  // null until the saved markers have loaded: the tab then opens on the
  // "Opt-in Copy Change" marker (its stored date) when it exists and can be
  // shown, otherwise on 'Last 7 days' exactly as before. Nothing is fetched
  // while it is null, so the tab does not load 7 days first and then again.
  const [periodId, setPeriodId] = useState(null);
  // ONE anchor for every block in a run, captured when the period changes. If
  // each block called Date.now() itself they would drift apart by however long
  // the fetches took, and the blocks would stop being equal.
  const [anchorMs, setAnchorMs] = useState(() => Date.now());
  const [results, setResults]   = useState({});   // block index -> {data|error}
  const [openSections, setOpenSections] = useState({ optin: true, masterclass: true, sales: true });
  const runIdRef = useRef(0);
  const scrollerRef = useRef(null);
  // The date header lives OUTSIDE the horizontal scroller (see THE GRID) and
  // tracks it by hand: this is the translated strip of date cells.
  const headerColsRef = useRef(null);
  const rootRef = useRef(null);

  // ── HOW FAR DOWN THE VIEWPORT THE DATE HEADER STICKS ──────────────────────
  // AdminDashboard's top bar ("Back to Portal · Admin Dashboard") is
  // position:sticky top:0, so a header stuck at top:0 would slide in BEHIND it.
  // Its height is not exported anywhere and depends on font metrics and on
  // whether its two halves have wrapped onto two lines, so it is MEASURED, not
  // hardcoded: the nearest sticky-at-top:0 element among the ancestors'
  // children OUTSIDE this component (the search starts above rootRef, so it
  // can never pick up the date header itself), watched with a ResizeObserver
  // so a wrap or a window resize re-measures it. Falls back to 0 (still
  // usable, just tucked under the bar) rather than guessing.
  const [stickyTop, setStickyTop] = useState(0);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let el = root.parentElement;
    let bar = null;
    while (el && el !== document.body && !bar) {
      bar = [...el.children].find(c => !c.contains(root) && getComputedStyle(c).position === 'sticky'
        && getComputedStyle(c).top === '0px') || null;
      el = el.parentElement;
    }
    if (!bar) { setStickyTop(0); return; }
    const measure = () => setStickyTop(Math.round(bar.getBoundingClientRect().height));
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(bar);
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  // Keep the date cells over their columns while the body scrolls sideways.
  // A transform, not a second scrollbar: the header strip is overflow:hidden
  // and is simply shifted by the body's scrollLeft on every scroll event.
  const syncHeader = useCallback(() => {
    const body = scrollerRef.current, cols = headerColsRef.current;
    if (!body || !cols) return;
    cols.style.transform = `translateX(${-body.scrollLeft}px)`;
  }, []);

  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { markers, loaded: markersLoaded } = useMarkers();
  const markerPeriods = useMemo(() => markers.map(markerPeriod), [markers]);

  const [defaultTimedOut, setDefaultTimedOut] = useState(false);
  useEffect(() => {
    if (periodId !== null) return undefined;
    if (markersLoaded || defaultTimedOut) {
      const m = markersLoaded ? findMarkerByName(markers, DEFAULT_MARKER_NAME) : null;
      const mp = m ? markerPeriod(m) : null;
      setAnchorMs(Date.now());
      setPeriodId(mp && isAvailable(mp, Date.now()) ? mp.id : '7d');
      return undefined;
    }
    const t = setTimeout(() => setDefaultTimedOut(true), 4000);
    return () => clearTimeout(t);
  }, [periodId, markersLoaded, defaultTimedOut, markers]);

  const period = PERIODS.find(p => p.id === periodId) || markerPeriods.find(p => p.id === periodId) || PERIODS[0];
  const built = useMemo(
    () => (period.marker ? buildMarkerBlocks(period.marker, anchorMs) : { blocks: buildBlocks(period, anchorMs), chunk: null }),
    [period, anchorMs]
  );
  const blocks = built.blocks;

  const choose = useCallback((p) => {
    if (!isAvailable(p, Date.now())) return;      // greyed options do nothing
    setAnchorMs(Date.now());
    setPeriodId(p.id);
  }, []);

  const removeMarker = async (m) => {
    if (!window.confirm(`Delete the marker "${m.name}"?`)) return;
    try {
      await deleteMarker(m.id);
      if (periodId === markerRangeId(m)) choose(PERIODS[0]);   // never nothing selected
    } catch {
      window.alert('Marker not deleted — check your connection and try again.');
    }
  };

  useEffect(() => {
    const runId = ++runIdRef.current;
    setResults({});
    if (periodId === null) return undefined;   // default period not decided yet
    if (!vw.ready && !vw.failed) return undefined;   // the per-video rows need the video periods first
    let cancelled = false;

    const fetchBlock = async (b) => {
      // allSettled: one dead endpoint must not blank the column.
      const [l, f, s, w] = await Promise.allSettled([
        getAdminLeadsStats(b.fromISO, b.toISO, true),
        getAdminFunnelPageStats(b.fromISO, b.toISO, true),
        getAdminRySalesStats(b.fromISO, b.toISO, true),
        getAdminWistiaStats(b.fromISO, b.toISO, true),
      ]);
      const pick = (r) => (r.status === 'fulfilled' ? r.value.data : null);
      // The sales video rows: each video over its own live part of this block,
      // with the same two requests the old switch sent for it.
      const videos = {};
      let videoFailed = false;
      for (const v of vw.videos) {
        const win = salesWindow({ ...v, launch: vw.info.launch }, vw.info.launch, b.fromISO, b.toISO);
        if (win.empty) { videos[v.id] = null; continue; }
        const [vf, vwi] = await Promise.allSettled([
          getAdminFunnelPageStats(win.from, win.to, true, salesExtra(v.id, win)),
          getAdminWistiaStats(win.from, win.to, true, salesExtra(v.id, win, { withVideo: true })),
        ]);
        if ([vf, vwi].some(r => r.status === 'rejected')) videoFailed = true;
        videos[v.id] = { funnel: pick(vf), wistia: pick(vwi) };
      }
      const anyFailed = [l, f, s, w].some(r => r.status === 'rejected') || videoFailed;
      return {
        leads: pick(l), funnel: pick(f), sales: pick(s), wistia: pick(w),
        videos,
        error: anyFailed ? 'some figures unavailable' : null,
      };
    };

    // ── AT MOST 8 REQUESTS IN FLIGHT ──────────────────────────────────────
    // Each block is 4 requests, so two blocks at a time. 12 columns would be 48
    // requests fired at once otherwise, which Render's free tier will not enjoy.
    // Columns render as they land rather than waiting for the slowest.
    (async () => {
      const CONCURRENT_BLOCKS = 2;
      // Newest column first. It renders on the right and is the one Ben reads
      // first, so it should not be last to arrive just because it sorts last.
      const order = blocks.map((_, i) => i).reverse();
      let next = 0;
      const worker = async () => {
        while (!cancelled) {
          const pos = next++;
          if (pos >= order.length) return;
          const idx = order[pos];
          try {
            const data = await fetchBlock(blocks[idx]);
            if (cancelled || runIdRef.current !== runId) return;
            setResults(prev => ({ ...prev, [idx]: data }));
          } catch (e) {
            if (cancelled || runIdRef.current !== runId) return;
            setResults(prev => ({ ...prev, [idx]: { error: e?.message || 'failed' } }));
          }
        }
      };
      await Promise.all(Array.from({ length: CONCURRENT_BLOCKS }, worker));
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, vw.ready, vw.failed, periodId === null]);

  const valueFor = (row, idx) => {
    const r = results[idx];
    if (!r || r.error === 'failed') return undefined;      // still loading / dead
    // Sales rows cannot report before attribution began.
    if (row.sales && blocks[idx].toMs < SALES_FROM_MS) return 'preAttribution';
    return row.get(r);
  };

  const renderValue = (row, v) => {
    if (v === undefined) return <span style={{ color: C.border }}>·</span>;
    if (v === 'preAttribution') return <span style={{ color: C.border }}>—</span>;
    if (v == null) return <span style={{ color: C.border }}>—</span>;
    if (row.kind === 'rate') {
      if (row.shareOfOptins) return shareOfOptinsText(v);
      return row.decimalsUnder10 ? optinRateText(v) : `${Math.round(v)}%`;
    }
    return Number(v).toLocaleString('en-AU');
  };

  // The raw count beside a rate. Same loading/missing marks as renderValue.
  const countFor = (row, idx) => {
    const r = results[idx];
    if (!r || r.error === 'failed') return undefined;
    return row.count(r);
  };
  const renderCount = (c) => {
    if (c === undefined) return <span style={{ color: C.border }}>·</span>;
    if (c == null) return <span style={{ color: C.border }}>—</span>;
    return Number(c).toLocaleString('en-AU');
  };

  const anySalesSuppressed = blocks.some(b => b.toMs < SALES_FROM_MS);
  const loadedCount = Object.keys(results).length;
  const allLoaded = blocks.every((_, i) => results[i] !== undefined);

  // ── THE TWO ROWS THAT HIDE THEMSELVES ────────────────────────────────────
  // "Couldn't open checkout" and "Already enrolled" are marked hideIfAllZero.
  // They disappear only when they are zero (or missing) in EVERY column of the
  // selected period — never per column. A row that vanished from some columns
  // and not others would leave gaps mid-row, which is exactly what the original
  // build avoided by showing them unconditionally.
  //
  // The test re-runs on every period change, because a row that is flat across
  // 7 days may well not be across 3 weeks.
  //
  // While columns are still arriving they stay HIDDEN, not shown. Judging on a
  // partial grid would show the row, then pull it out from under the reader as
  // the last zero landed.
  // A per-video row becomes one row per sales video that has ever been live.
  const expandPerVideo = (row) => (row.perVideo
    ? vw.videos.map(v => ({
        ...row, label: `${row.label} — video ${v.label}`,
        get: d => (d.videos && d.videos[v.id] ? videoRate(d.videos[v.id], row.perVideo) : null),
      }))
    : [row]);

  const isRowVisible = (row) => {
    if (!row.hideIfAllZero) return true;
    if (!allLoaded) return false;
    return blocks.some((_, i) => {
      const v = valueFor(row, i);
      return typeof v === 'number' && v !== 0;
    });
  };

  // Re-pin to the right whenever the period changes or a column lands and
  // widens the grid. Only while the run is still filling in — once every column
  // has arrived this stops, so it can never fight a scroll Ben has made himself.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || loadedCount === 0 || loadedCount > blocks.length) return;
    el.scrollLeft = el.scrollWidth;
    syncHeader();
  }, [loadedCount, blocks.length, syncHeader]);

  return (
    <div ref={rootRef} style={{ fontFamily: FONT, color: C.text }}>
      <SectionHeader
        title="Compare"
        sub="The Ad Metrics figures in equal blocks side by side — paid traffic only. Blocks roll backwards from now in exact equal lengths, so no column is a partial period."
      />

      {/* ── PERIOD PICKER ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        {PERIODS.map(p => {
          const live = isAvailable(p, anchorMs);
          const on   = p.id === periodId;
          return (
            <div key={p.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button
                onClick={() => choose(p)}
                disabled={!live}
                title={live ? undefined : `Needs data back to ${fmtFull(anchorMs - totalMs(p))}, before tracking began`}
                style={{
                  padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: FONT,
                  border: `1px solid ${on ? C.heading : C.border}`,
                  background: on ? C.heading : C.card,
                  color: !live ? C.border : on ? '#FFF8F7' : C.accent,
                  cursor: live ? 'pointer' : 'not-allowed',
                  opacity: live ? 1 : 0.55,
                }}
              >{p.label}</button>
              {/* Not hardcoded — floor + the period's own total length. */}
              {!live && (
                <span style={{ fontSize: 9, color: C.accent, opacity: 0.8, marginTop: 2 }}>
                  available {fmtShort(availableFromMs(p))}
                </span>
              )}
            </div>
          );
        })}
        {/* Ben's own markers, from the same list the other tabs read. */}
        {markerPeriods.map(p => {
          const live = isAvailable(p, anchorMs);
          return (
            <MarkerChip key={p.id} marker={p.marker} on={p.id === periodId} disabled={!live}
              title={live ? `Since ${fmtFull(markerStartMs(p.marker))}` : 'Dated before tracking began'}
              onSelect={() => choose(p)}
              onDelete={isOwner ? () => removeMarker(p.marker) : undefined} />
          );
        })}
        {isOwner && <AddMarkerControl onCreated={(m) => choose(markerPeriod(m))} />}
      </div>

      <div style={{ fontSize: 10.5, color: C.accent, marginBottom: 14, lineHeight: 1.55 }}>
        {period.marker && built.chunk && (
          <><strong style={{ color: C.heading }}>Since {period.marker.name}</strong>
            {' '}· {built.chunk.word} columns from {fmtFull(markerStartMs(period.marker))}, {blocks.length} in all.{' '}</>
        )}
        Each column compares against the one to its left. A period is greyed until its oldest
        block starts on or after 14 Aug 2026, when tracking began — before that the figures
        would cover a shorter window than the label claims.
        {loadedCount < blocks.length && (
          <> {' '}<strong style={{ color: C.heading }}>Loading {loadedCount} of {blocks.length} columns…</strong></>
        )}
      </div>

      {/* ── THE GRID ────────────────────────────────────────────────────── */}
      {/* Opens scrolled to the far right — the most recent block. On a phone the
          newest column is off-screen by default now that time runs left to
          right, and that is the column Ben looks at first. The label column is
          position:sticky, so it stays pinned regardless of scroll offset and
          this does not fight it. */}
      {/* ── THE DATE HEADER — STUCK TO THE VIEWPORT ───────────────────────
          Scroll a few rows into any section and a column of figures with no
          date over it means nothing, so the dates must be on screen at every
          scroll position. position:sticky INSIDE the horizontal scroller
          cannot do that: overflow-x:auto makes the scroller a scroll container
          on both axes, so a sticky header sticks to the scroller — which is as
          tall as its content and rides away with the page.

          So the header sits OUTSIDE the scroller, as its sibling, stuck to the
          window below the dashboard's top bar (stickyTop, measured). Nothing
          between here and the viewport has an overflow, so it holds. The
          "Metric" label cell is a fixed column on the left, exactly where the
          body's sticky-left label cells sit; the date cells are one strip in
          an overflow:hidden viewport, shifted by the body's scrollLeft (see
          syncHeader), so they stay over their columns as Ben scrolls
          sideways. Same cells, same widths, same wording as before — only
          where they are mounted has changed. The header carries the grid's
          top border, corners and background so rows never show through. */}
      <div
        style={{ position: 'sticky', top: stickyTop, zIndex: 3,
          display: 'flex', background: C.card,
          border: `1px solid ${C.border}`, borderBottom: `2px solid ${C.border}`,
          borderRadius: '12px 12px 0 0' }}
      >
        <div style={{ ...labelCell, position: 'static', fontSize: 10, fontWeight: 800, color: C.accent,
          textTransform: 'uppercase', letterSpacing: '0.06em', borderRadius: '12px 0 0 0' }}>Metric</div>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div ref={headerColsRef} style={{ display: 'flex', width: 'max-content', willChange: 'transform' }}>
            {blocks.map((b, i) => (
              <div key={b.back} style={{ ...dataCell, fontSize: 10 }}>
                {/* The date the block STARTS, so the headers read forwards in
                    time from left to right. "−2 days" style labels counted
                    backwards, which reads the wrong way round once the columns
                    are chronological. */}
                <div style={{ fontWeight: 800, color: C.heading }}>
                  {fmtShort(b.fromMs)}
                  {b.isLatest && (
                    <span style={{ fontWeight: 600, color: C.accent, opacity: 0.8 }}> (now)</span>
                  )}
                </div>
                <div style={{ color: C.accent, marginTop: 2 }}>
                  {fmtShort(b.fromMs)} – {fmtShort(b.toMs)}
                </div>
                {(b.partial || b.extended) && (
                  <div style={{ color: '#92650A', marginTop: 2 }}>
                    {b.partial ? 'partial' : 'extended'} · {fmtLen(b.lengthMs)}
                  </div>
                )}
                {!results[i] && <div style={{ color: C.border, marginTop: 2 }}>loading…</div>}
                {results[i]?.error && <div style={{ color: '#A8674F', marginTop: 2 }}>partial</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div ref={scrollerRef} onScroll={syncHeader}
        style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderTop: 'none',
          borderRadius: '0 0 12px 12px', background: C.card }}>
        <div style={{ minWidth: 'max-content' }}>

          {SECTIONS.map(sec => {
            const rows = sec.rows.flatMap(expandPerVideo).filter(isRowVisible);
            return (
            <div key={sec.id}>
              <div
                onClick={() => setOpenSections(o => ({ ...o, [sec.id]: !o[sec.id] }))}
                style={{ display: 'flex', cursor: 'pointer', background: '#FBF6F2',
                  borderBottom: `1px solid ${C.border}`, borderTop: `1px solid ${C.border}` }}
              >
                <div style={{ ...labelCell, background: '#FBF6F2', fontSize: 11, fontWeight: 800, color: C.heading }}>
                  {openSections[sec.id] ? '▾' : '▸'} {sec.title}
                </div>
                <div style={{ flex: 1 }} />
              </div>

              {openSections[sec.id] && rows.map((row, ri) => (
                <div key={row.label} style={{ display: 'flex',
                  borderBottom: ri === rows.length - 1 ? 'none' : `1px solid #F3EAE2` }}>
                  <div style={{ ...labelCell, fontSize: 11, color: C.text }}>{row.label}</div>
                  {blocks.map((b, i) => {
                    const v = valueFor(row, i);
                    // The block immediately OLDER is the column to the LEFT.
                    const older = i > 0 ? valueFor(row, i - 1) : undefined;
                    const comparable = typeof v === 'number' && typeof older === 'number';
                    const change = comparable ? changeFor(row, v, older) : null;
                    return (
                      <div key={b.back} style={dataCell}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.heading, whiteSpace: 'nowrap' }}>
                          {row.count ? (
                            // Count first, rate second and smaller. While the
                            // column is still loading both are undefined, so
                            // one '·' is shown rather than two.
                            v === undefined
                              ? renderValue(row, v)
                              : <>
                                  {renderCount(countFor(row, i))}
                                  {/* A real space, not only a margin — so the
                                      pair reads "14 44%" when copied or read
                                      aloud, not "1444%". */}
                                  {' '}
                                  <span style={{ fontSize: 10, fontWeight: 600, color: C.accent, marginLeft: 2 }}>
                                    {renderValue(row, v)}
                                  </span>
                                </>
                          ) : renderValue(row, v)}
                        </div>
                        {/* The oldest block has nothing older to compare to, and
                            it is now the LEFTMOST column. */}
                        {i > 0 && (
                          <div style={{ marginTop: 2 }}><Change change={change} /></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              {openSections[sec.id] && sec.footnote && (
                <div style={{ display: 'flex', borderTop: `1px solid #F3EAE2` }}>
                  <div style={{ ...labelCell, borderRight: 'none', flex: '1 1 auto', width: 'auto',
                    position: 'static', fontSize: 9.5, color: C.accent, fontStyle: 'italic', lineHeight: 1.5 }}>
                    {sec.footnote}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {anySalesSuppressed && (
        <div style={{ fontSize: 10, color: C.accent, marginTop: 10, lineHeight: 1.55 }}>
          Sales rows show a dash for any column ending before 16 Aug 2026. Sales attribution began
          then — earlier buyers carry no visitor id, so a zero there would read as a collapse when
          nothing was being measured.
        </div>
      )}

      <div style={{ fontSize: 10, color: C.accent, marginTop: 8, lineHeight: 1.55 }}>
        Counts change in per cent; rates change in percentage points. A change is only shown when
        the older column is 5 or more — below that the movement is noise.
      </div>
    </div>
  );
};
